/**
 * Custom hook for accessing audit history data
 * 
 * In demo mode: Uses localStorage via auditService
 * In live mode: Fetches from backend API via approvalsApi (StageExecution table)
 */

import { useMemo, useState, useEffect } from 'react';
import { auditService } from '@/services/auditService';
import { approvalsApi } from '@/services/approvalsApi';
import { useDataSource } from '@/contexts/DataSourceContext';
import type { AuditEntry, AuditSummary, AuditActionType } from '@/types/audit';
import type { TimesheetWorkflowHistory, ApprovalResponseHistory, WorkflowEventHistory, RuleEvaluationLog } from '@/types/workflow-status';

/**
 * Maps a workflow history response to audit entries
 */
export function mapWorkflowHistoryToAuditEntries(history: TimesheetWorkflowHistory): AuditEntry[] {
  const entries: AuditEntry[] = [];

  // Add workflow started / submitted entry
  // ENG-002: Backend now stores submittedByEmployeeName on WorkflowInstance
  if (history.startedAt) {
    entries.push({
      id: `workflow-started-${history.workflowInstanceId}`,
      timesheetId: history.timesheetId,
      timestamp: history.startedAt,
      actionType: 'submitted',
      actorId: 'system',
      actorName: history.submittedByEmployeeName || 'System',
      actorRole: 'employee',
      comment: history.workflowName ? `Submitted to workflow "${history.workflowName}"` : 'Submitted for approval',
    });
  }

  // ENG-003: Map workflow-level events (recalls, resubmissions) to audit entries
  if (history.events) {
    for (const event of history.events) {
      let actionType: AuditActionType;
      if (event.eventType === 'recalled') {
        actionType = 'recalled';
      } else if (event.eventType === 'resubmitted') {
        actionType = 'resubmitted';
      } else {
        actionType = event.eventType as AuditActionType;
      }

      entries.push({
        id: event.id,
        timesheetId: history.timesheetId,
        timestamp: event.timestamp,
        actionType,
        actorId: event.actorId.toString(),
        actorName: event.actorName || 'Unknown',
        actorRole: 'employee',
        comment: event.reason || undefined,
        previousStatus: event.eventType === 'recalled' ? 'submitted' : undefined,
        newStatus: event.eventType === 'recalled' ? 'draft' : undefined,
      });
    }
  }

  // Map each stage's approval responses to audit entries
  for (const stage of history.stages) {
    // Create audit entry for skipped stages
    if (stage.status === 'skipped') {
      entries.push({
        id: `stage-skipped-${stage.id}`,
        timesheetId: history.timesheetId,
        timestamp: stage.startedAt,
        actionType: 'stage_skipped',
        actorId: 'system',
        actorName: 'System',
        actorRole: 'system',
        comment: stage.skippedReason || `Stage '${stage.name}' was skipped`,
      });
    }

    // Create audit entries for rule evaluations that changed routing
    if (stage.ruleEvaluationLogs) {
      for (const log of stage.ruleEvaluationLogs) {
        if (log.conditionMet && log.actionTaken && log.actionTaken !== 'Continue') {
          entries.push({
            id: `rule-${stage.id}-${log.ruleId}`,
            timesheetId: history.timesheetId,
            timestamp: new Date(log.evaluatedAt),
            actionType: 'rule_triggered',
            actorId: 'system',
            actorName: 'Smart Rule',
            actorRole: 'system',
            comment: `Rule '${log.ruleName}' triggered: ${log.actionTaken}`,
          });
        }
      }
    }

    for (const response of stage.responses) {
      let actionType: AuditActionType;
      if (response.decision === 'approver_edited') {
        actionType = 'approver_edited';
      } else if (response.decision === 'approved') {
        actionType = 'approved';
      } else {
        actionType = 'rejected';
      }
      
      const actorName = response.approverName || 'Unknown';
      const actorRole = 'approver';

      entries.push({
        id: response.id,
        timesheetId: history.timesheetId,
        timestamp: response.respondedAt,
        actionType,
        actorId: response.approverId,
        actorName,
        actorRole,
        comment: response.comments || undefined,
        // approver_edited doesn't change the timesheet status
        previousStatus: actionType === 'approver_edited' ? undefined : 'submitted',
        newStatus: actionType === 'approver_edited' ? undefined : (response.decision === 'approved' ? 'approved' : 'rejected'),
        // Delegation metadata — propagated from backend ApprovalResponse
        isDelegated: response.isDelegated || false,
        delegatedFromManagerName: response.delegatedFromName || undefined,
        delegatedFromManagerId: response.delegatedFromId || undefined,
      });
    }
  }

  return entries;
}

/**
 * Creates an audit summary from workflow history
 */
function mapWorkflowHistoryToAuditSummary(history: TimesheetWorkflowHistory): AuditSummary {
  const summary: AuditSummary = {
    totalChanges: 0,
  };

  // ENG-002: submittedByEmployeeName is now available from the backend
  if (history.startedAt) {
    summary.submittedAt = history.startedAt;
    summary.submittedBy = history.submittedByEmployeeName || undefined;
    summary.totalChanges++;
  }

  // ENG-003: Map recall events into the summary
  if (history.events) {
    for (const event of history.events) {
      summary.totalChanges++;
      if (event.eventType === 'recalled') {
        summary.recalledAt = event.timestamp;
        summary.recalledBy = event.actorName || 'Unknown';
        summary.recallReason = event.reason || undefined;
      }
    }
  }

  // Count skipped stages and rule evaluations
  for (const stage of history.stages) {
    if (stage.status === 'skipped') {
      summary.totalChanges++;
    }
    if (stage.ruleEvaluationLogs) {
      for (const log of stage.ruleEvaluationLogs) {
        if (log.conditionMet && log.actionTaken && log.actionTaken !== 'Continue') {
          summary.totalChanges++;
        }
      }
    }
  }

  // Find approval/rejection from stage responses
  for (const stage of history.stages) {
    for (const response of stage.responses) {
      summary.totalChanges++;
      
      if (response.decision === 'approved') {
        // Use the latest approval
        if (!summary.approvedAt || response.respondedAt > summary.approvedAt) {
          summary.approvedAt = response.respondedAt;
          summary.approvedBy = response.approverName || 'Unknown';
          // Carry delegation context into summary
          summary.approvalIsDelegated = response.isDelegated || false;
          summary.approvalDelegatedFromManagerName = response.isDelegated ? (response.delegatedFromName || undefined) : undefined;
        }
      } else if (response.decision === 'rejected') {
        // Use the latest rejection
        if (!summary.rejectedAt || response.respondedAt > summary.rejectedAt) {
          summary.rejectedAt = response.respondedAt;
          summary.rejectedBy = response.approverName || 'Unknown';
          // Carry delegation context into summary
          summary.rejectionIsDelegated = response.isDelegated || false;
          summary.rejectionDelegatedFromManagerName = response.isDelegated ? (response.delegatedFromName || undefined) : undefined;
        }
      } else if (response.decision === 'approver_edited') {
        // Track last modification by approver
        if (!summary.lastModifiedAt || response.respondedAt > summary.lastModifiedAt) {
          summary.lastModifiedAt = response.respondedAt;
          summary.lastModifiedBy = response.approverName || 'Unknown';
        }
      }
    }
  }

  return summary;
}

export function useAuditHistory(timesheetId: number, open = true) {
  const { getScreenMode } = useDataSource();
  // Use per-screen mode for timesheet screen instead of global mode
  const isLive = getScreenMode('timesheet') === 'live';
  const [liveEntries, setLiveEntries] = useState<AuditEntry[]>([]);
  const [liveSummary, setLiveSummary] = useState<AuditSummary>({ totalChanges: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch from API in live mode
  useEffect(() => {
    // Skip when dialog is closed or for draft timesheets with temporary IDs
    if (!open || !isLive || !timesheetId || timesheetId <= 0) {
      setLiveEntries([]);
      setLiveSummary({ totalChanges: 0 });
      return;
    }

    let cancelled = false;

    async function fetchWorkflowHistory() {
      setIsLoading(true);
      setError(null);
      
      try {
        const history = await approvalsApi.getWorkflowHistory(timesheetId);
        
        if (cancelled) return;

        const entries = mapWorkflowHistoryToAuditEntries(history);
        const summary = mapWorkflowHistoryToAuditSummary(history);
        
        setLiveEntries(entries);
        setLiveSummary(summary);
      } catch (err) {
        if (cancelled) return;
        
        console.error('Failed to fetch workflow history:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch history');
        setLiveEntries([]);
        setLiveSummary({ totalChanges: 0 });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchWorkflowHistory();

    return () => {
      cancelled = true;
    };
  }, [isLive, timesheetId, open]);

  // Demo mode: use localStorage via auditService
  const demoEntries = useMemo(() => {
    if (isLive) return [];
    return auditService.getAuditTrail(timesheetId);
  }, [isLive, timesheetId, open]);

  const demoSummary = useMemo(() => {
    if (isLive) return { totalChanges: 0 };
    return auditService.getAuditSummary(timesheetId);
  }, [isLive, timesheetId, open]);

  // Select entries based on mode
  const auditEntries = isLive ? liveEntries : demoEntries;
  const auditSummary = isLive ? liveSummary : demoSummary;

  const sortedEntries = useMemo(() => {
    return [...auditEntries].sort((a, b) => 
      b.timestamp.getTime() - a.timestamp.getTime()
    );
  }, [auditEntries]);

  return {
    auditEntries: sortedEntries,
    auditSummary,
    hasHistory: auditEntries.length > 0,
    isLoading,
    error,
  };
}
